import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import MainContainer from "../../layout/MainContainer";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { ENUM_ROUTES } from "../../RoutesEnum";
import { useGetVenueQuery } from "../../services/venue";
import { showError, showToast } from "../../constants/toasts";
import {
  useAddDiscountMutation,
  useLazyGetDiscountDetailQuery,
  useUpdateDiscountMutation,
} from "../../services/discount";
import { removeFalsyKeys } from "../../utils/nullKey";
import { CATEGORY_TYPE } from "../../utils/enums";

const discountTypeOptions = ["PERCENTAGE", "FLAT"];

const initialValues = {
  name: "",
  couponCode: "",
  couponValue: "",
  discountType: "",
  minBookingAmount: "",
  startDate: dayjs().format("YYYY-MM-DD"),
  endDate: dayjs().add(1, "day").format("YYYY-MM-DD"),
  type: "",
  venueStorageId: [],
};
const today = new Date();
today.setHours(0, 0, 0, 0);
const createValidationSchema = (isEdit: boolean) =>
  Yup.object({
    name: Yup.string().required("Name is required"),
    couponCode: Yup.string().required("Coupon code is required"),
    discountType: Yup.string().required("Discount type is required"),
    minBookingAmount: Yup.number()
      .min(0, "Minimum bookings must be 0 or greater")
      .required("Minimum bookings is required"),
    startDate: Yup.date()
      .transform((value, originalValue) => (originalValue === "" || originalValue === undefined ? undefined : value))
      .required("Start date is required")
      .min(today, "Start date cannot be in the past"),

    endDate: Yup.date()
      .transform((value, originalValue) => (originalValue === "" || originalValue === undefined ? undefined : value))
      .required("End date is required")
      .when("startDate", (startDate, schema) => {
        if (startDate) {
          return schema.min(startDate, "End date cannot be before start date");
        }
        return schema;
      }),
    venueStorageId: Yup.array()
      .of(Yup.string().required("Invalid value"))
      .min(1, "This field is required")
      .required("This field is required"),
    couponValue: Yup.number()
      .typeError("Coupon value must be a number")
      .required("Coupon value is required")
      .when("discountType", {
        is: "PERCENTAGE",
        then: (schema) =>
          schema.max(100, "Percentage discount cannot exceed 100"),
        otherwise: (schema) => schema,
      }),
  });

const AddDiscount = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [addDiscount, { isLoading }] = useAddDiscountMutation();
  const [updateDiscount] = useUpdateDiscountMutation();
  const [discountData, setDiscountData] = useState<any>(null);
  const [discountDetails] = useLazyGetDiscountDetailQuery();
  const [loading, setLoading] = useState(false);

  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const response = await discountDetails({ id }).unwrap();
      if (response.statusCode === 200) {
        setDiscountData(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };



  const formik = useFormik({
    initialValues,
    validationSchema: createValidationSchema(!!id),
    onSubmit: async (values, { setSubmitting }) => {

      try {
        const payload: any = {
          name: values.name,
          couponCode: values.couponCode,
          discountType: values.discountType,
          minBookingAmount: values.minBookingAmount,
          startDate: new Date(values.startDate).toISOString(),
          endDate: new Date(values.endDate).toISOString(),
          type: values.type,
          couponValue: values.couponValue,
          ...(formik.values.type == CATEGORY_TYPE.VENUE ? { venueStorageId: formik.values.venueStorageId } : { venueStorageId: formik.values.venueStorageId })
        };
        console.log(payload, "Data");
        removeFalsyKeys(payload);
        let response;
        if (id) {
          response = await updateDiscount({ body: payload, id }).unwrap();
        } else {
          response = await addDiscount(payload).unwrap();
        }
        if (response?.statusCode === 200) {
          showToast(
            id ? "Discount updated successfully" : "Discount added successfully"
          );
          navigate("/discount-coupon-management");
        }
      } catch (error: any) {
        console.error("Error details:", error);
        showError(
          error?.message || error?.data?.message || "Failed to save discount"
        );
      } finally {
        setSubmitting(false);
      }
    },
  });
  const { data: venueList } = useGetVenueQuery({
    limit: 100,
    page: 1,
    search: "",
    type: formik.values.type === CATEGORY_TYPE.VENUE ? CATEGORY_TYPE.VENUE : CATEGORY_TYPE.STORAGE,
    status: "APPROVED",
  }, { skip: !formik.values.type });
  useEffect(() => {
    if (id) {
      getDataByIdList();
    }
  }, [id]);

  useEffect(() => {
    if (discountData) {

      formik.setValues({
        name: discountData.name || "",
        couponCode: discountData.couponCode || "",
        discountType: discountData.discountType || "PERCENTAGE",
        minBookingAmount: discountData.minBookingAmount || 0,
        startDate: discountData.startDate
          ? dayjs(discountData.startDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        endDate: discountData.endDate
          ? dayjs(discountData.endDate).format("YYYY-MM-DD")
          : dayjs().add(1, "day").format("YYYY-MM-DD"),
        type: discountData.type || "",
        couponValue: discountData.couponValue || "",
        venueStorageId: discountData.type === CATEGORY_TYPE.VENUE ? (discountData?.venueId || []) : (discountData?.venueStorageId || [])
      });
    }
  }, [discountData]);
  if (loading) {
    return (
      <MainContainer>
        <div className="main_title">
          <h1>Loading...</h1>
        </div>
      </MainContainer>
    );
  }



  return (
    <MainContainer>
      <div className="main_title">
        <h1>{id ? "Edit" : "Add"} Discount</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span
            onClick={() => navigate(ENUM_ROUTES.DISCOUNT_COUPON_MANAGEMENT)}
          >
            Discount Management
          </span>
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            {/* Name */}
            <div className="control_group w_50">
              <label>Coupon Name</label>
              <TextField
                fullWidth
                name="name"
                placeholder="Enter Discount Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>

            {/* Coupon Code */}
            <div className="control_group w_50">
              <label>Coupon Code</label>
              <TextField
                fullWidth
                name="couponCode"
                placeholder="Enter Coupon Code"
                value={formik.values.couponCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.couponCode && Boolean(formik.errors.couponCode)}
                helperText={formik.touched.couponCode && formik.errors.couponCode}
              />
            </div>

            {/* Discount Type */}
            <div className="control_group w_50">
              <label>Discount Type</label>
              <FormControl
                fullWidth
                error={
                  formik.touched.discountType &&
                  Boolean(formik.errors.discountType)
                }
              >
                <Select
                  name="discountType"
                  displayEmpty
                  value={formik.values.discountType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <em>Select Discount Type</em>;
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="">
                    <em>Select Discount Type</em>
                  </MenuItem>
                  {discountTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.discountType && formik.errors.discountType && (
                  <Box className="error_text">{formik.errors.discountType}</Box>
                )}
              </FormControl>
            </div>
            <div className="control_group w_50">
              <label>Coupon Value</label>
              <TextField
                fullWidth
                name="couponValue"
                placeholder="Enter Coupon Value"
                value={formik.values.couponValue}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.couponValue && Boolean(formik.errors.couponValue)}
                helperText={formik.touched.couponValue && formik.errors.couponValue}
              />
            </div>
            {/* Applicable Time */}
            {/* <div className="control_group w_50">
              <label>Applicable Time</label>
              <FormControl
                fullWidth
                error={
                  formik.touched.applicableTime &&
                  Boolean(formik.errors.applicableTime)
                }
              >
                <Select
                  name="applicableTime"
                  displayEmpty
                  value={formik.values.applicableTime}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <em>Select Applicable Time</em>;
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="">
                    <em>Select Applicable Time</em>
                  </MenuItem>
                  {applicableTimeOptions.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.applicableTime &&
                  formik.errors.applicableTime && (
                    <Box className="error_text">
                      {formik.errors.applicableTime}
                    </Box>
                  )}
              </FormControl>
            </div> */}


            {/* Minimum Bookings */}
            <div className="control_group w_50">
              <label>Minimum Bookings</label>
              <TextField
                fullWidth
                type="number"
                name="minBookingAmount"
                placeholder="Enter Minimum Bookings"
                value={formik.values.minBookingAmount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.minBookingAmount &&
                  Boolean(formik.errors.minBookingAmount)
                }
                helperText={
                  formik.touched.minBookingAmount && formik.errors.minBookingAmount
                }
              />
            </div>

            {/* Start Date */}
            <div className="control_group w_25">
              <label>Start Date</label>
              <TextField
                type="date"
                name="startDate"
                fullWidth
                value={formik.values.startDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                error={
                  formik.touched.startDate && Boolean(formik.errors.startDate)
                }
                helperText={formik.touched.startDate && formik.errors.startDate}
              />
            </div>

            {/* End Date */}
            <div className="control_group w_25">
              <label>End Date</label>
              <TextField
                type="date"
                name="endDate"
                fullWidth
                value={formik.values.endDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                helperText={formik.touched.endDate && formik.errors.endDate}
              />
            </div>
            <div className="control_group w_50">
              <label>Select Type</label>
              <FormControl
                fullWidth
              >
                <Select
                  name="type"
                  placeholder="type"
                  value={formik.values.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    formik.setFieldValue("type", newType);

                    // reset venueStorageId whenever type changes
                    formik.setFieldValue("venueStorageId", []);
                  }}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    <em>Select Type</em>
                  </MenuItem>
                  <MenuItem value={CATEGORY_TYPE.VENUE}>Venue</MenuItem>
                  <MenuItem value={CATEGORY_TYPE.STORAGE}>Storage</MenuItem>
                </Select>
              </FormControl>
            </div>
            {formik.values.type && (
              <div className="control_group w_50">
                <label>  {formik.values.type === CATEGORY_TYPE.VENUE ? "Venue" : "Storage"}</label>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.venueStorageId &&
                    Boolean(formik.errors.venueStorageId)
                  }

                >
                  <Select
                    name="venueStorageId"
                    displayEmpty
                    multiple
                    value={formik.values.venueStorageId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    input={<OutlinedInput label="Venue" />}
                    renderValue={(selected) => {
                      if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                        return formik.values.type === CATEGORY_TYPE.VENUE ? "Please Select Venue" : "Please Select Storage";
                      }

                      // Ensure selected is an array of ids
                      const selectedIds: any = Array.isArray(selected) ? selected : [selected];

                      const selectedVenues = venueList?.data?.data?.filter((venue: any) =>
                        selectedIds.includes(venue?._id)
                      );

                      return selectedVenues?.map((venue) => venue.name).join(", ");
                    }}
                  >
                    {venueList?.data?.data?.map((venue) => (
                      <MenuItem key={venue._id} value={venue._id}>
                        {venue.name}
                      </MenuItem>
                    ))}
                  </Select>

                  {formik.touched.venueStorageId &&
                    formik.errors.venueStorageId && (
                      <Box className="error_text">
                        {formik.errors.venueStorageId}
                      </Box>
                    )}
                </FormControl>
              </div>
            )}

          </div>

          {/* Save Button */}
          <div className="form_btn">
            <Button
              className="btn btn_primary"
              type="submit"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div >
    </MainContainer >
  );
};

export default AddDiscount;
